import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { userApi, authApi } from '@/services/api'
import { useAuthStore } from '@/store/auth.store'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { formatDate } from '@/utils/format'
import { Search, Edit, Trash2, Shield, Plus, UserCog, Eye, Info } from 'lucide-react'
import type { User } from '@/types'

export default function UserList() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    user_type: 'professional',
    profession: '',
    phone_number: '',
    continent: '',
    country: '',
    city: ''
  })

  const { data, isLoading } = useQuery({
    queryKey: ['users', { search, user_type: typeFilter, subscription_tier: tierFilter }],
    queryFn: () => userApi.list({ search, user_type: typeFilter, subscription_tier: tierFilter }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => userApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setSuccess('User deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete user')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => userApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowModal(false)
      setEditingUser(null)
      setSuccess('User updated successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update user')
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => userApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreateModal(false)
      setNewUser({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        user_type: 'professional',
        profession: '',
        phone_number: '',
        continent: '',
        country: '',
        city: ''
      })
      setSuccess('User created successfully. They will need an active subscription to access the platform.')
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create user')
    },
  })

  const impersonateMutation = useMutation({
    mutationFn: (userId: number) => authApi.impersonate(userId),
    onSuccess: (response) => {
      // Update auth store with impersonated user
      if (response.data) {
        const { setAuth } = useAuthStore.getState()
        setAuth(response.data.user, response.data.token)
        setSuccess('Now impersonating user. Redirecting...')
        setTimeout(() => {
          navigate('/dashboard')
          window.location.reload() // Reload to update all components with new user context
        }, 1000)
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to impersonate user')
    },
  })

  const users = data?.data?.users || []

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setShowModal(true)
  }

  const handleUpdate = () => {
    if (!editingUser) return
    updateMutation.mutate({
      id: editingUser.id,
      data: {
        user_type: editingUser.user_type,
        subscription_status: editingUser.subscription_status,
      },
    })
  }

  const handleCreate = () => {
    setError('')

    // Validate required fields
    if (!newUser.first_name || !newUser.last_name || !newUser.email || !newUser.password) {
      setError('Please fill in all required fields')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newUser.email)) {
      setError('Please enter a valid email address')
      return
    }

    // Validate password length
    if (newUser.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    createMutation.mutate(newUser)
  }

  const handleImpersonate = (user: User) => {
    if (window.confirm(`Are you sure you want to impersonate ${user.first_name} ${user.last_name}?`)) {
      impersonateMutation.mutate(user.id)
    }
  }

  const canImpersonate = currentUser?.user_type === 'super_admin' && !currentUser?.is_impersonating

  if (isLoading) return <Loading />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">User Management</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <Card>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                className="input pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <select
            className="select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="professional">Professional</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <select
            className="select"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
          >
            <option value="">All Tiers</option>
            <option value="free">Free Tier</option>
            <option value="paid">Paid Tier</option>
          </select>
        </div>

        <ResponsiveTable>
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hide-mobile" data-priority="3">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hide-mobile" data-priority="1">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hide-mobile" data-priority="4">Subscription</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hide-mobile" data-priority="2">Coins</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hide-mobile" data-priority="5">Joined</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {users.map((user: User) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {user.business_name && (
                        <div className="text-xs text-gray-400">{user.business_name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hide-mobile" data-label="Type" data-priority="3">
                    <Badge
                      variant={
                        user.user_type === 'super_admin'
                          ? 'danger'
                          : user.user_type === 'admin'
                          ? 'warning'
                          : 'primary'
                      }
                    >
                      <Shield className="w-3 h-3 inline mr-1" />
                      {user.user_type?.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hide-mobile" data-label="Location" data-priority="1">
                    {user.city || 'Any'}, {user.country || 'Any'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hide-mobile" data-label="Subscription" data-priority="4">
                    {user.user_type === 'professional' ? (
                      <div className="space-y-1">
                        <Badge
                          variant={user.subscription_status === 'active' ? 'success' : 'danger'}
                        >
                          {user.subscription_status}
                        </Badge>
                        <div className="text-xs text-gray-500">
                          Tier: <span className="font-medium capitalize">{user.subscription_tier || 'free'}</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium hide-mobile" data-label="Coins" data-priority="2">
                    {user.barter_coin_balance || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hide-mobile" data-label="Joined" data-priority="5">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button className="mobile-details-trigger" title="View Details">
                        <Info className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/dashboard/users/${user.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Profile"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canImpersonate && user.user_type !== 'super_admin' && (
                        <button
                          onClick={() => handleImpersonate(user)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Impersonate User"
                        >
                          <UserCog className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>

        {users.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </Card>

      {/* Edit User Modal */}
      {showModal && editingUser && (
        <Modal
          title="Edit User"
          onClose={() => {
            setShowModal(false)
            setEditingUser(null)
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="label">User Name</label>
              <input
                type="text"
                className="input"
                value={`${editingUser.first_name} ${editingUser.last_name}`}
                disabled
              />
            </div>

            <div>
              <label className="label">Email</label>
              <input type="email" className="input" value={editingUser.email} disabled />
            </div>

            <div>
              <label className="label">User Type</label>
              <select
                className="select"
                value={editingUser.user_type}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, user_type: e.target.value as any })
                }
              >
                <option value="professional">Professional</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            {editingUser.user_type === 'professional' && (
              <div>
                <label className="label">Subscription Status</label>
                <select
                  className="select"
                  value={editingUser.subscription_status || 'inactive'}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, subscription_status: e.target.value as any })
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowModal(false)
                  setEditingUser(null)
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdate} isLoading={updateMutation.isPending}>
                Update User
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <Modal
          title="Create New User"
          onClose={() => {
            setShowCreateModal(false)
            setNewUser({
              first_name: '',
              last_name: '',
              email: '',
              password: '',
              user_type: 'professional',
              profession: '',
              phone_number: '',
              continent: '',
              country: '',
              city: ''
            })
          }}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name *</label>
                <input
                  type="text"
                  className="input"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="label">Last Name *</label>
                <input
                  type="text"
                  className="input"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                className="input"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john.doe@example.com"
                required
              />
            </div>

            <div>
              <label className="label">Password *</label>
              <input
                type="password"
                className="input"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Minimum 8 characters"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="label">User Type *</label>
              <select
                className="select"
                value={newUser.user_type}
                onChange={(e) => setNewUser({ ...newUser, user_type: e.target.value })}
              >
                <option value="professional">Professional</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            <div>
              <label className="label">Profession</label>
              <input
                type="text"
                className="input"
                value={newUser.profession}
                onChange={(e) => setNewUser({ ...newUser, profession: e.target.value })}
                placeholder="e.g., Web Developer"
              />
            </div>

            <div>
              <label className="label">Phone Number</label>
              <input
                type="tel"
                className="input"
                value={newUser.phone_number}
                onChange={(e) => setNewUser({ ...newUser, phone_number: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Continent</label>
                <input
                  type="text"
                  className="input"
                  value={newUser.continent}
                  onChange={(e) => setNewUser({ ...newUser, continent: e.target.value })}
                  placeholder="North America"
                />
              </div>
              <div>
                <label className="label">Country</label>
                <input
                  type="text"
                  className="input"
                  value={newUser.country}
                  onChange={(e) => setNewUser({ ...newUser, country: e.target.value })}
                  placeholder="USA"
                />
              </div>
              <div>
                <label className="label">City</label>
                <input
                  type="text"
                  className="input"
                  value={newUser.city}
                  onChange={(e) => setNewUser({ ...newUser, city: e.target.value })}
                  placeholder="New York"
                />
              </div>
            </div>

            {newUser.user_type === 'professional' && (
              <Alert
                type="info"
                message="Note: Professional users will need to activate a subscription before they can access the platform features."
              />
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreateModal(false)
                  setNewUser({
                    first_name: '',
                    last_name: '',
                    email: '',
                    password: '',
                    user_type: 'professional',
                    profession: '',
                    phone_number: '',
                    continent: '',
                    country: '',
                    city: ''
                  })
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreate} isLoading={createMutation.isPending}>
                Create User
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
